import React, { useEffect, useState, useRef } from 'react';
import { Button, Alert } from 'antd';
import axios from '../common/axios';
import MathJaxHTMLContent from '../common/MathJaxHTMLContent';
import CircularLoader from "../common/CircularLoader";
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const WalkthroughStep = ({ step, stepActivity, onBack, continueCurrentStep, setShowSummary }) => {
    const [question, setQuestion] = useState();
    const [stepQuestion, setStepQuestion] = useState();
    const [stepResult, setStepResult] = useState();
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [validationError, setValidationError] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [stepNo, setStepNo] = useState(1);
    const [attemptCount, setAttemptCount] = useState(1);

    const feedbackRef = useRef(null);
    const formRef = useRef();

    const loadWalkthroughStep = async () => {
        try {
            const response = await axios.get('/web/ui/walkthrough/trail/walkthrough/step', {
                params: {stepId: step.stepId, conceptId: step.conceptId, questionId: stepActivity.questionId, stepNo},
            });
            setQuestion(response.data.question);
            setStepQuestion(response.data.stepQuestion);
        } catch (err) {
            console.error(err);
            setError('Failed to load walkthrough step.');
        } finally {
            setLoading(false);
        }
    };

    const handleLoadNextStep = () => {
        setStepResult(undefined);
        setError(undefined);
        setValidationError(undefined);
        setAttemptCount(1);
        setStepNo(stepNo => stepNo + 1);
    };

    const handleTryAgain = () => {
        setStepResult(undefined);
        setError(undefined);
        setValidationError(undefined);
        setSubmitting(false);
    };

    useEffect(() => {
        if(!step || !stepActivity || !stepNo) {
            //setLoading(false);
            return;
        }

        loadWalkthroughStep();
    }, [step, stepActivity, stepNo]);

    useEffect(() => {
        if (stepResult?.feedback && feedbackRef.current) {
            feedbackRef.current.focus();
        }
    }, [stepResult]);

    const validateInputs = (formEl) => {
        const inputs = formEl.querySelectorAll('input, select, textarea');
        for (let input of inputs) {
            if (input.type === 'radio' || input.type === 'checkbox') {
                const group = formEl.querySelectorAll(`input[name="${input.name}"]`);
                const anyChecked = Array.from(group).some(i => i.checked);
                if (!anyChecked) return false;
            } else if (!input.value?.trim()) {
                return false;
            }
        }
        return true;
    };

    const serializeForm = (formEl) => {
        const formData = new FormData(formEl);
        const result = {};
        for (let [key, value] of formData.entries()) {
            if (!result[key]) result[key] = [];
            result[key].push(value);
        }
        return result;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(undefined);
        setValidationError(undefined);

        const formEl = formRef.current;

        if (!validateInputs(formEl)) {
            setValidationError('Please complete all required inputs before submitting.');
            return;
        }

        setSubmitting(true);
        const requestBody = serializeForm(formEl);
        try {
            const response = await axios.post(
                '/web/ui/walkthrough/trail/step/answer',
                requestBody,
                {
                    params: {
                        questionId: stepActivity.questionId,
                        conceptId: step.conceptId,
                        stepQuestionId: stepQuestion.questionId,
                        attemptCount,
                    },
                    headers: { 'Content-Type': 'application/json' },
                }
            );

            const data = response.data;
            setStepResult(data);
            setAttemptCount(data.attemptCount ?? attemptCount + 1);
        } catch (err) {
            console.error(err)
            setError(err.response?.data?.message || 'Error submitting  answer');
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoading) return <CircularLoader />;
    if(!question || !stepQuestion) return <Alert type="error" message="step in invalid" role="alert" />
    if (error) return <Alert type="error" message={error} role="alert" />;

    const stepTitle = `Step ${stepNo} of ${question?.childQuestionCount ?? 1}`;

    return (
        <section className="ch-trail-walkthrough-step" aria-labelledby="walkthrough-title">
            <header>
                <h4 id="walkthrough-title">{stepTitle}</h4>
            </header>
            <form
                ref={formRef}
                className={`${stepResult ? 'ch-question-question-disabled' : ''}`}
                onSubmit={handleSubmit}
                aria-describedby={validationError ? 'validation-message' : undefined}
            >
                <MathJaxHTMLContent html={stepQuestion.formattedInstructions} showLoader={false}/>

                {validationError && (
                    <div className="mediumGap">
                        <Alert
                            type="warning"
                            message={validationError}
                            showIcon
                            role="alert"
                            tabIndex={-1}
                            id="validation-message"
                            aria-live="assertive"
                        />
                    </div>
                )}

                {!stepResult && (
                    <div className="flex flexJustifyStart mediumGap">
                        <Button
                            type="primary"
                            htmlType="submit"
                            disabled={submitting}
                            aria-label="Submit walkthrough answer"
                        >
                            Answer
                        </Button>
                    </div>
                )}
            </form>

            {stepResult && (
                <div
                    className="ch-question-feedback"
                    role="region"
                    aria-label="Question Feedback"
                    tabIndex="-1"
                    ref={feedbackRef}
                >
                    <div className="ch-question-feedback-content flexColumn mediumGap">
                        <div className="flexAlignCentre smallGap" aria-live="polite">
                            {stepResult.result ? (
                                <>
                                    <CheckCircleOutlined className="ch-correct-icon" aria-hidden="true" />
                                    <strong>Correct</strong>
                                </>
                            ) : (
                                <>
                                    <CloseCircleOutlined className="ch-incorrect-icon" aria-hidden="true" />
                                    <strong>Incorrect</strong>
                                </>
                            )}
                        </div>
                        <MathJaxHTMLContent html={stepResult.feedback} showLoader={false} />
                    </div>
                </div>
            )}

            {stepResult && (
                <div className="ch-question-feedback-buttons mediumGap">
                    {!stepResult.result && attemptCount <= 2 ? (
                        <Button
                            onClick={handleTryAgain}
                            aria-label="Try the question again"
                            type="primary"
                        >
                            Try Again
                        </Button>
                    ) : question?.childQuestionCount === stepNo ? (
                        <>
                            <Button
                                type="default"
                                aria-label="View summary of walkthrough"
                                onClick={() => setShowSummary(true)}
                            >
                                Summary
                            </Button>
                            <Button
                                type="primary"
                                aria-label="Continue to next section"
                                onClick={continueCurrentStep}
                            >
                                Continue
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={handleLoadNextStep}
                            aria-label="Go to next step"
                            type="primary"
                        >
                            Next Step
                        </Button>
                    )}
                </div>
            )}
        </section>
    );
};

export default WalkthroughStep;
